import React, { useEffect, useRef, useState } from 'react';
import './searchBar.css';

const SearchBar = ({
    placeholder = "Search...",
    value,
    onChange,
    onSearch,
    className = "",
    containerStyle = {},
    debounceTime = 1000,
    onFocusChange
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const isFirstRun = useRef(true);
    const searchTimeoutRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-search effect with debounce
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        searchTimeoutRef.current = setTimeout(() => {
            if (onSearch) onSearch(value);
        }, debounceTime);

        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [value, onSearch, debounceTime]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            if (onSearch) onSearch(value);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        onChange("");
        if (onSearch) onSearch("");
        if (inputRef.current) inputRef.current.focus();
    };

    const handleFocus = () => {
        setIsFocused(true);
        if (onFocusChange) onFocusChange(true);
    };

    const handleBlur = () => {
        // Small delay to prevent closing before clear button click is processed
        // Increased slightly for mobile keyboard transitions
        setTimeout(() => {
            if (document.activeElement !== inputRef.current) {
                setIsFocused(false);
                if (onFocusChange) onFocusChange(false);
            }
        }, 200);
    };

    const handleContainerClick = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    return (
        <div
            className={`search-bar-container ${isFocused ? 'is-focused' : ''} ${className}`}
            style={containerStyle}
            onClick={handleContainerClick}
        >
            <div className="search-bar-inner">
                <i className="bi bi-search search-icon"></i>
                <input
                    ref={inputRef}
                    type="text"
                    className="search-input"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
                {value && (
                    <i
                        className="bi bi-x-circle-fill clear-icon"
                        onClick={handleClear}
                    ></i>
                )}
            </div>
        </div>
    );
};

export default SearchBar;
